import { useRef, useEffect, useCallback } from "react";
import { Bold, Italic, Underline, Heading2, Heading3, Quote, List, ListOrdered, Link2, Image as ImageIcon, Undo, Redo } from "lucide-react";

export function RichTextEditor({ value, onChange, onPickImage }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = useCallback(() => {
    if (ref.current) onChange(ref.current.innerHTML);
  }, [onChange]);

  const exec = (cmd, val = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, val);
    emit();
  };

  const block = (tag) => exec("formatBlock", tag);

  const addLink = () => {
    const url = window.prompt("Link-URL eingeben:", "https://");
    if (url) exec("createLink", url);
  };

  const addImage = async () => {
    let url;
    if (onPickImage) url = await onPickImage();
    else url = window.prompt("Bild-URL eingeben:", "https://");
    if (url) exec("insertImage", url);
  };

  const Btn = ({ onClick, title, children, testid }) => (
    <button type="button" data-testid={testid} title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className="w-9 h-9 flex items-center justify-center text-prussian hover:bg-offwhite border border-transparent hover:border-edborder rounded transition-colors">
      {children}
    </button>
  );

  return (
    <div className="border border-edborder bg-white">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-edborder p-1.5 bg-offwhite/60 sticky top-0 z-10">
        <Btn onClick={() => exec("bold")} title="Fett" testid="rte-bold"><Bold size={16} /></Btn>
        <Btn onClick={() => exec("italic")} title="Kursiv" testid="rte-italic"><Italic size={16} /></Btn>
        <Btn onClick={() => exec("underline")} title="Unterstrichen" testid="rte-underline"><Underline size={16} /></Btn>
        <span className="w-px h-6 bg-edborder mx-1" />
        <Btn onClick={() => block("<h2>")} title="Zwischentitel" testid="rte-h2"><Heading2 size={16} /></Btn>
        <Btn onClick={() => block("<h3>")} title="Untertitel" testid="rte-h3"><Heading3 size={16} /></Btn>
        <Btn onClick={() => block("<blockquote>")} title="Zitat" testid="rte-quote"><Quote size={16} /></Btn>
        <Btn onClick={() => block("<p>")} title="Absatz" testid="rte-p"><span className="text-xs font-bold">P</span></Btn>
        <span className="w-px h-6 bg-edborder mx-1" />
        <Btn onClick={() => exec("insertUnorderedList")} title="Liste" testid="rte-ul"><List size={16} /></Btn>
        <Btn onClick={() => exec("insertOrderedList")} title="Nummerierte Liste" testid="rte-ol"><ListOrdered size={16} /></Btn>
        <span className="w-px h-6 bg-edborder mx-1" />
        <Btn onClick={addLink} title="Link" testid="rte-link"><Link2 size={16} /></Btn>
        <Btn onClick={addImage} title="Bild einfügen" testid="rte-image"><ImageIcon size={16} /></Btn>
        <span className="w-px h-6 bg-edborder mx-1" />
        <Btn onClick={() => exec("undo")} title="Rückgängig" testid="rte-undo"><Undo size={16} /></Btn>
        <Btn onClick={() => exec("redo")} title="Wiederholen" testid="rte-redo"><Redo size={16} /></Btn>
      </div>
      <div
        ref={ref}
        data-testid="rte-content"
        className="rte-content p-4"
        contentEditable
        suppressContentEditableWarning
        data-placeholder="Artikeltext hier verfassen…"
        onInput={emit}
        onBlur={emit}
      />
    </div>
  );
}
